import type { Question } from "../../types.js";

type Seed = Omit<Question, "rating" | "conceptId"> & { rating: number };

const raw: readonly Seed[] = [
  {
    id: "fa00",
    rating: 950,
    text: "Before touching an injured person, you should first…",
    correctIndex: 0,
    options: [
      "Check that the scene is safe for you and them",
      "Start chest compressions immediately",
      "Move them to your car",
      "Give them water to drink",
    ],
  },
  {
    id: "fa01",
    rating: 980,
    text: "If you see smoke or an electrical hazard near a victim, you should…",
    correctIndex: 1,
    options: [
      "Ignore it and keep helping",
      "Get to a safe place and call for help",
      "Pour water on the wires",
      "Remove the victim through the smoke without looking",
    ],
  },
  {
    id: "fa02",
    rating: 1000,
    text: "For heavy bleeding from an arm, a good first step is often…",
    correctIndex: 2,
    options: [
      "Wait to see if it stops on its own",
      "Remove any bandage every minute",
      "Firm pressure on the wound with a clean cloth",
      "Wait for swelling to go down first",
    ],
  },
  {
    id: "fa03",
    rating: 1050,
    text: "Elevating a bleeding limb can help when…",
    correctIndex: 0,
    options: [
      "It is safe to do so and does not worsen their pain or injury",
      "The person has fainted",
      "You have no cloth for pressure",
      "The wound is only a small scrape",
    ],
  },
  {
    id: "fa04",
    rating: 1100,
    text: "When calling emergency services, it helps most to…",
    correctIndex: 3,
    options: [
      "Speak as fast as possible",
      "Describe a medical diagnosis you are not sure of",
      "Hang up if they put you on hold",
      "Give location, what happened, and the person’s condition",
    ],
  },
  {
    id: "fa05",
    rating: 1150,
    text: "Recovery position is most appropriate for someone who is…",
    correctIndex: 0,
    options: [
      "Unconscious but breathing and with no other position ordered by a responder",
      "Not breathing and you are alone with no help coming",
      "Having a small nosebleed while sitting up",
      "Standing and feeling dizzy in a hot room",
    ],
  },
  {
    id: "fa06",
    rating: 1150,
    text: "The recovery position mainly helps to…",
    correctIndex: 0,
    options: [
      "Keep the airway open if the person is breathing",
      "Stop all bleeding by itself",
      "Replace professional treatment",
      "Fix suspected spinal injury without help",
    ],
  },
  {
    id: "fa07",
    rating: 1200,
    text: "If you are unsure whether to move someone with possible neck pain, you should…",
    correctIndex: 2,
    options: [
      "Twist the neck to “loosen it”",
      "Pull them to standing quickly",
      "Follow your training: avoid moves that can worsen injury and get help",
      "Roll them into recovery position if they ask politely",
    ],
  },
  {
    id: "fa08",
    rating: 1250,
    text: "A sign that bleeding may need emergency care is…",
    correctIndex: 0,
    options: [
      "Spurting blood or bleeding that will not stop with pressure",
      "A small scratch that stopped",
      "A bruise that appears the next day",
      "A paper cut with no blood",
    ],
  },
  {
    id: "fa09",
    rating: 1280,
    text: "On an emergency call, the dispatcher may ask for…",
    correctIndex: 1,
    options: [
      "Only your name",
      "Your exact location, phone number, and what is happening",
      "A detailed medical school lecture",
      "The victim’s last five years of life history",
    ],
  },
];

const conceptById: Readonly<Record<string, string>> = {
  fa00: "scene_safety",
  fa01: "scene_safety",
  fa02: "bleeding_basic",
  fa03: "bleeding_basic",
  fa04: "emergency_call",
  fa05: "recovery_position",
  fa06: "recovery_position",
  fa07: "scene_safety",
  fa08: "bleeding_basic",
  fa09: "emergency_call",
};

function conceptForFaqId(id: string): string {
  return conceptById[id] ?? "scene_safety";
}

export const FIRST_AID_SEED_QUESTIONS: readonly Question[] = raw.map(
  (q): Question => ({
    id: q.id,
    conceptId: conceptForFaqId(q.id),
    rating: q.rating,
    text: q.text,
    correctIndex: q.correctIndex,
    options: q.options as [string, string, string, string],
    source: "seed",
  })
);
